import React from "react";
import Image from "@qlik/qlik/src/components/Image";
import Cta from "@qlik/qlik/src/components/Cta";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getSafeMediaAssetUrl, renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";

const CardItemContent = ({ cardItem }) => (
    <>
        {cardItem.eyebrowText && (
            <p className={`tag-2 text-xxs md:text-sm mb-2.5 md:mb-3.75 uppercase`}>{cardItem.eyebrowText}</p>
        )}
        {cardItem.title && (
            <h3
                className={`text-base md:title-5 tracking-normal line-clamp-none mb-3.5`}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(cardItem.title) }}
            />
        )}
        {cardItem.subtitle && (
            <h5
                className={`body text-blue mb-3.75`}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(cardItem.subtitle) }}
            />
        )}
        {cardItem?.description?.description && (
            <p className={`rich-text-body hasLinks`}>{cardItem.description.description}</p>
        )}
        {/* CTA rendering is handled in CardCollectionStacked */}
    </>
);

const CardCollectionStacked = ({ cardCollection, applyEqualHeight }) => {
    const { mediaAsset, cardItems = [] } = cardCollection ?? {};
    const logo = getSafeMediaAssetUrl(mediaAsset);

    return (
        // Ensure this component fills the height allocated by the parent grid cell
        <div className="flex flex-col gap-0 h-full">
            {logo && <Image link={logo} className="rounded-t shadow-lg flex-shrink-0" />}

            <div
                className={`rounded-b shadow-lg bg-white p-5 lg:p-10 flex flex-col ${
                    applyEqualHeight ? "flex-grow" : ""
                }`}
            >
                {cardItems.map((cardItem, index) => (
                    <React.Fragment key={cardItem.id}>
                        <div className="flex flex-col flex-grow min-h-0">
                            <div className="flex-grow">
                                <CardItemContent cardItem={cardItem} />
                            </div>
                            <div className="mt-6">
                                {cardItem?.cta?.map((button) => (
                                    <Cta key={button.id} data={button} className="py-3.75 tracking-normal" />
                                ))}
                            </div>
                        </div>
                        {index < cardItems.length - 1 && <hr className="my-8 flex-shrink-0 text-gray-light" />}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

// GroupedCards component remains the same
const GroupedCards = ({ data }) => {
    const { additionalConfiguration, cardCollections, customId } = data ?? {};
    const spacingClassName = getSpacingClassName(additionalConfiguration, "pt-15 pb-14 md:py-20 lg:py-30");

    // Determine if all collections have the same number of cards
    let applyEqualHeight = false;
    if (cardCollections && cardCollections.length > 1) {
        // Check if all collections have the same number of cards
        // If the first collection has no cards, we can't apply equal height
        const firstCollectionCount = cardCollections[0]?.cardItems?.length ?? 0;
        // Check if all collections have the same number of cards
        applyEqualHeight = cardCollections.every(
            (collection) => (collection?.cardItems?.length ?? 0) === firstCollectionCount
        );
    } else if (cardCollections && cardCollections.length <= 1) {
        // If there's only one collection, we can apply equal height
        applyEqualHeight = true;
    }

    return (
        <section className={`${spacingClassName}`} id={customId}>
            <div className="container">
                <div className="grid md:grid-cols-2 gap-10">
                    {cardCollections?.map((collection) => (
                        <CardCollectionStacked
                            key={collection.id}
                            cardCollection={collection}
                            applyEqualHeight={applyEqualHeight}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default GroupedCards;
